import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";

export const ProductModal = ({ product, onClose }) => {
  const { addItem } = useCart();
  const { fmt } = useCurrency();
  const hasSizes = product.sizes && product.sizes.length > 0;
  const [size, setSize] = useState(hasSizes ? "" : null);
  const [qty, setQty] = useState(1);

  const add = () => {
    if (hasSizes && !size) {
      toast.error("Please select a size");
      return;
    }
    addItem(product, size, qty);
    toast.success(`${product.name} added to cart`);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[92] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} data-testid="product-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#0C1310] border border-white/10 rounded-2xl grid md:grid-cols-2"
        >
          <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center rounded-full bg-black/60 border border-white/20 text-white hover:bg-emerald-500 hover:text-emerald-950 transition-colors" data-testid="product-modal-close">
            <X size={20} />
          </button>

          <div className="bg-white flex items-center justify-center p-6">
            <img src={product.image} alt={product.name} className="w-full h-full max-h-[420px] object-contain" />
          </div>

          <div className="p-7 sm:p-9">
            <p className="font-accent text-lg tracking-[0.3em] text-emerald-400">OFFICIAL MERCH</p>
            <h3 className="font-display text-3xl sm:text-4xl text-white mt-1">{product.name}</h3>
            <p className="text-amber-500 font-display text-3xl mt-2">{fmt(product.price)}</p>
            <p className="text-gray-300 mt-4 leading-relaxed">{product.description}</p>

            {hasSizes && (
              <div className="mt-6">
                <p className="font-accent text-lg tracking-widest text-gray-400 mb-2">SELECT SIZE</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`px-4 py-2 rounded-md border font-semibold text-sm transition-colors ${size === s ? "bg-emerald-500 border-emerald-500 text-emerald-950" : "bg-white/5 border-white/15 text-gray-300 hover:border-emerald-400"}`}
                      data-testid={`size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <p className="font-accent text-lg tracking-widest text-gray-400 mb-2">QUANTITY</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 grid place-items-center rounded bg-white/10 text-white hover:bg-white/20" data-testid="qty-dec"><Minus size={16} /></button>
                <span className="text-white text-lg w-8 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(99, qty + 1))} className="w-9 h-9 grid place-items-center rounded bg-white/10 text-white hover:bg-white/20" data-testid="qty-inc"><Plus size={16} /></button>
              </div>
            </div>

            <button onClick={add} className="w-full btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wide py-3 mt-8 glow-green transition-transform hover:scale-[1.02]" data-testid="add-to-cart-btn">
              <span className="flex items-center justify-center gap-2"><Check size={18} /> Add to Cart</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
